import type { HttpQueryParam, HttpRequestConfig, IHttpClient } from "@/core/interfaces";
import {
  extractDetail,
  extractItems,
  extractTotal,
  isRecord,
  readText,
  toPaginatedQuery,
} from "@/core/utils";
import {
  appendExportColumns,
  appendReportQuery,
  parseContentDispositionFileName,
} from "@/modules/export";
import type { ExportedFile } from "@/modules/export";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import { AUDIT_EXPORT_COLUMN_KEYS } from "./audit.constants";
import { DEFAULT_AUDIT_SORT } from "./audit.types";
import type {
  AuditFilters,
  AuditRecord,
  AuditRecordsPage,
  ExportAuditRecordsParams,
  GetAuditRecordParams,
  ListAuditRecordsParams,
} from "./audit.types";

export const AUDIT_PATHS = {
  LIST: "/api/plataforma/auditoria",
  EXPORT: "/api/plataforma/auditoria/exportar",
} as const;

/** Recurso do detalhe de um registro; conforme o item 834, apenas GET. */
export function buildAuditRecordPath(id: string): string {
  return `${AUDIT_PATHS.LIST}/${encodeURIComponent(id)}`;
}

const COLLECTION_KEYS = ["registros", "auditoria"];
const DETAIL_KEYS = ["registro", "auditoria"];

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

function readTrimmed(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const text = readText(raw[key]).trim();
    if (text) return text;
  }

  return "";
}

/**
 * Valor anterior ou novo: texto (formato antigo) ou objeto `{ id, nome }` (HU 1556); lista de objetos vira os nomes
 * separados por vírgula. O id técnico nunca aparece.
 */
function readValue(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = raw[key];
    const text = Array.isArray(value)
      ? value.map(readValueName).filter(Boolean).join(", ")
      : isRecord(value)
        ? readValueName(value)
        : readText(value).trim();
    if (text) return text;
  }

  return "";
}

function readValueName(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (!isRecord(value)) return "";

  return readTrimmed(value, "nome", "name", "label", "descricao", "codigo");
}

/**
 * O período de/até vai em ISO 8601 do primeiro instante do dia de início ao último do dia de fim, no fuso do usuário,
 * para que os dois dias inteiros caibam no intervalo. Só vale completo (início e fim).
 */
export function buildAuditPeriod(
  dataInicio: string,
  dataFim: string,
): { dataInicio: string; dataFim: string } | null {
  const inicio = parseLocalDate(dataInicio);
  const fim = parseLocalDate(dataFim);
  if (!inicio || !fim) return null;

  fim.setHours(23, 59, 59, 999);

  return { dataInicio: inicio.toISOString(), dataFim: fim.toISOString() };
}

function parseLocalDate(value: string): Date | null {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

function toAuditRecord(raw: unknown, index: number): AuditRecord | null {
  if (!isRecord(raw)) return null;

  const dataHora = readTrimmed(raw, "dataHora", "data_hora", "createdAt", "created_at", "data");
  const acao = readTrimmed(raw, "acao", "action", "alteracao");
  const modulo = readTrimmed(raw, "modulo", "module");
  const responsavelBruto = readTrimmed(raw, "responsavel", "responsible", "usuario", "createdBy");
  const responsavel = readTrimmed(raw, "responsavelNome") || responsavelBruto;
  if (!dataHora && !acao && !modulo && !responsavel) return null;

  const registroAlterado = isRecord(raw.registroAlterado) ? raw.registroAlterado : {};

  return {
    // O índice mantém a linha endereçável (chave da tabela, ação Detalhar) sem id.
    id: readTrimmed(raw, "id", "auditoriaId") || `${dataHora}-${index}`,
    dataHora,
    acao,
    acaoDescricao: readTrimmed(raw, "acaoDescricao"),
    valorAnterior: readValue(raw, "valorAnterior", "valor_anterior", "previousValue"),
    valorNovo: readValue(raw, "valorNovo", "valor_novo", "newValue"),
    justificativa: readTrimmed(raw, "justificativa", "justification"),
    justificativaLegivel: readTrimmed(raw, "justificativaLegivel"),
    modulo,
    moduloSistema: readTrimmed(raw, "moduloSistema"),
    responsavel,
    responsavelCargo: readTrimmed(raw, "responsavelCargo"),
    responsavelPerfil: readTrimmed(raw, "responsavelPerfil", "responsavel_perfil"),
    responsavelDetalhe: readTrimmed(
      raw,
      "responsavelPerfil",
      "responsavel_perfil",
      "perfil",
      "cargo",
      "area",
      "setor",
    ),
    responsavelEmail: readTrimmed(
      raw,
      "responsavelEmail",
      "responsavel_email",
      "email",
      "usuarioEmail",
    ),
    responsavelTelefone: readTrimmed(
      raw,
      "responsavelTelefone",
      "responsavel_telefone",
      "telefone",
      "phone",
      "celular",
    ),
    registroAlteradoTipo: readTrimmed(registroAlterado, "tipo"),
    registroAlteradoNome: readTrimmed(registroAlterado, "nome"),
    registroAlteradoEmail: readTrimmed(registroAlterado, "email"),
    registroAlteradoCodigo: readTrimmed(registroAlterado, "codigo"),
    registroAlteradoObra: readTrimmed(registroAlterado, "obra"),
  };
}

/** Critérios da consulta em parâmetros do serviço, iguais na listagem e na exportação. */
function buildFilterParams({
  dataInicio = "",
  dataFim = "",
  alteracao,
  modulo,
  responsavelId,
}: Partial<AuditFilters>): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = {};
  const periodo = buildAuditPeriod(dataInicio, dataFim);
  if (periodo) Object.assign(params, periodo);
  const acoes = (alteracao ?? []).filter((item) => item.trim() !== "");
  const responsaveis = (responsavelId ?? []).map((item) => item.trim()).filter(Boolean);
  // O filtro chama-se Alteração na tela, mas o serviço lê o código como `acao`. As listas vão sempre como lista (o bff
  // junta em CSV e o core separa), mesmo com um valor só.
  if (acoes.length > 0) params.acao = acoes;
  if (modulo) params.modulo = modulo;
  if (responsaveis.length > 0) params.responsavel = responsaveis;

  return params;
}

/**
 * Consulta somente leitura dos registros de auditoria. Os filtros se combinam em conjunto (período, `acao`, `modulo`
 * e `responsavel`); paginação e ordenação no contrato paginado (`page`, `pageSize`, `sort_by`, `sort_direction`), aplicadas pelo
 * servidor sobre o conjunto filtrado inteiro.
 */
export async function listAuditRecords({
  page,
  pageSize,
  sort = DEFAULT_AUDIT_SORT,
  signal,
  ...filters
}: ListAuditRecordsParams = {}): Promise<AuditRecordsPage> {
  const params: Record<string, HttpQueryParam> = buildFilterParams(filters);
  Object.assign(params, toPaginatedQuery({ page, pageSize, sort }));

  const config: HttpRequestConfig = { signal, params };

  const response = await getHttpClient().get<unknown>(AUDIT_PATHS.LIST, config);

  const records = extractItems(response.data, COLLECTION_KEYS)
    .map(toAuditRecord)
    .filter((record): record is AuditRecord => record !== null);

  return { records, total: extractTotal(response.data) ?? records.length };
}

/**
 * Exportar Visualização (HU 1442): o servidor gera o arquivo com os filtros e a ordenação em vigor, sem paginação, e
 * registra a exportação na auditoria. O nome vem do `Content-Disposition`.
 */
export async function exportAuditRecords({
  sort = DEFAULT_AUDIT_SORT,
  filtros,
  colunas,
  format,
  signal,
  ...filters
}: ExportAuditRecordsParams): Promise<ExportedFile> {
  const params: Record<string, HttpQueryParam> = {
    ...buildFilterParams(filters),
    ...toPaginatedQuery({ sort }),
    formato: format,
  };
  appendExportColumns(params, colunas, AUDIT_EXPORT_COLUMN_KEYS, "Auditoria");
  appendReportQuery(params, filtros);

  const response = await getHttpClient().get<ArrayBuffer>(AUDIT_PATHS.EXPORT, {
    signal,
    params,
    responseType: "arraybuffer",
  });

  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}

/**
 * Detalhe somente leitura do registro acionado em Detalhar.
 *
 * Contrato do item 834: `GET /api/plataforma/auditoria/{id}`, sem nenhuma
 * escrita (RN004/RN005). Devolve `null` quando a resposta chega sem o registro
 * — a tela trata esse caso como registro não encontrado.
 */
export async function getAuditRecord({
  id,
  signal,
}: GetAuditRecordParams): Promise<AuditRecord | null> {
  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(buildAuditRecordPath(id), config);
  const record = toAuditRecord(extractDetail(response.data, DETAIL_KEYS), 0);

  // RN001 — o detalhe pertence à linha acionada: vale o identificador consultado.
  return record ? { ...record, id } : null;
}
